import { useRepairOrderQuotesStore } from '../../../repair-order-quotes-context';
import { useAvailabilityStore } from '../availability-store';
import styles from './styles.module.scss';
import { StockPart } from 'api-new/generated';
import { PartLookUpResult, RepairOrderQuotePartSubmitModel } from 'api/generated';
import { InlineSvg } from 'components/svg';
import { useAppStore } from 'features/settings/contexts/settings.context';
import { useCommonRepairOrderQuotePartMutation } from 'features/vhc/api/use-common-repair-order-quote-part.mutation';
import { usePartRequestsPartsLookupQuery } from 'features/vhc/api/use-part-pequests-parts-lookup.query';
import { usePartsBranchAvailabilityMutation } from 'features/vhc/api/use-parts-branch-availability.mutation';
import { SelectPartModal } from 'features/vhc/modals/select-part/modal';
import { RepairOrderQuoteItems } from 'features/vhc/pages/record/form-state';
import useToggle from 'hooks/use-toggle';
import { FC, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

type Props = {
  mainIndex: number;
  rowIndex: number;
};
export const PartNumberComponent: FC<Props> = ({ mainIndex, rowIndex }) => {
  const {
    state: { branches }
  } = useAppStore();
  const { setAvailabilityState } = useAvailabilityStore();
  const { isEditMode, initState } = useRepairOrderQuotesStore();

  const {
    register,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors }
  } = useFormContext<RepairOrderQuoteItems>();

  const isLoadedPartInfo = useRef(false);

  const quoteId = watch(`items.${mainIndex}.id`);
  const brandCode = watch(`items.${mainIndex}.parts.${rowIndex}.brandCode`);
  const partNumber = watch(`items.${mainIndex}.parts.${rowIndex}.partNumber`);
  const isDeleted = watch(`items.${mainIndex}.parts.${rowIndex}.isDeleted`);
  const isLookUped = watch(`items.${mainIndex}.parts.${rowIndex}.isLookUped`);

  const { isLoading: isLoadingSavePartLine, mutateAsync: savePartLinesAsync } = useCommonRepairOrderQuotePartMutation();
  const { isLoading: isLoadingBranch, mutate: mutateBranch } = usePartsBranchAvailabilityMutation();
  const [wipInfoBranch, setWipInfoBranch] = useState<number | undefined>(undefined);

  const { isFetching: isLoadingPartsLookup, refetch: refetchPartRequestsPartsLookupQuery } =
    usePartRequestsPartsLookupQuery(
      {
        brandCode: brandCode ?? '',
        partNumber: partNumber?.replace(/^0+(?!$)/, ''),
        branch: initState?.branch
      },
      [mainIndex, rowIndex, 'PartNo', brandCode, partNumber, wipInfoBranch],
      {
        enabled: !!brandCode && !!partNumber && !!wipInfoBranch,
        onSuccess: (data) => {
          if (!data.length) return;

          isLoadedPartInfo.current = true;

          if (data.length > 1) {
            openSelectPartsModelToggleState.setData(data);
            openSelectPartsModelToggleState.open();
          } else {
            applySelectedPart(data[0]);
          }
        }
      }
    );

  const applySelectedPart = async (part: PartLookUpResult) => {
    if (!part?.info) return;

    const currentPart = getValues(`items.${mainIndex}.parts.${rowIndex}`);
    let wipInfoId = currentPart?.wipInfoId;
    const partDescription = watch(`items.${mainIndex}.parts.${rowIndex}.description`);
    const newBrandCode = part.availability?.length ? part.availability[0].brandCode : part.info.brandCode;

    if (newBrandCode !== part.info.brandCode) {
      setValue(`items.${mainIndex}.parts.${rowIndex}.brandCode`, newBrandCode);
      trigger(`items.${mainIndex}.parts.${rowIndex}.brandCode`);
    }

    if (!currentPart?.id) {
      try {
        const requestBody: RepairOrderQuotePartSubmitModel = {
          brandCode: newBrandCode,
          description: part.info?.description ?? '',
          partNumber: part.info?.partCode,
          quantity: !currentPart.quantity ? 1 : currentPart.quantity,
          discount: currentPart.discount == null ? 0 : currentPart.discount,
          isManuallyFilled: false,
          isLookUped: true
        };

        if (part.availability?.length) {
          const availability = part.availability[0];

          requestBody.listPriceExclVAT = availability.listPrice?.netValue ?? 0;
          requestBody.listPriceInclVAT = availability.listPrice?.grossValue ?? 0;
          requestBody.surchargeExclVAT = currentPart.surchargeExclVAT ?? availability.surcharge?.netValue ?? 0;
          requestBody.surchargeInclVAT = currentPart.surchargeInclVAT ?? availability.surcharge?.grossValue ?? 0;
          requestBody.price = availability.listPrice?.grossValue;

          const quantity = requestBody.quantity ?? 1;
          const discount = requestBody.discount ?? 0;
          const total = availability.listPrice?.grossValue ?? 0 * quantity;
          const totalVAT = total - (discount / 100) * total;

          requestBody.totalExclVat = Number((totalVAT / 1.2).toFixed(2));
          requestBody.totalInclVat = Number(totalVAT.toFixed(2));
        }

        const response = await savePartLinesAsync({
          id: quoteId!,
          requestBody: requestBody
        });

        setValue(`items.${mainIndex}.parts.${rowIndex}.id`, response.partLineId);
        setValue(`items.${mainIndex}.parts.${rowIndex}.wipInfoId`, response.wipInfoId);
        setValue(`items.${mainIndex}.parts.${rowIndex}.isLookUped`, true);

        wipInfoId = response.wipInfoId;
      } catch (e) {
        console.error(e);
      }
    }

    if (partDescription !== part.info.description) {
      setValue(`items.${mainIndex}.parts.${rowIndex}.description`, part.info.description);
      trigger(`items.${mainIndex}.parts.${rowIndex}.description`);
    }

    if (partNumber !== part.info.partCode) {
      setValue(`items.${mainIndex}.parts.${rowIndex}.partNumber`, part.info.partCode);
      trigger(`items.${mainIndex}.parts.${rowIndex}.partNumber`);
    }

    setValue(`items.${mainIndex}.parts.${rowIndex}.isManuallyFilled`, false);
    setValue(`items.${mainIndex}.parts.${rowIndex}.isLookUped`, true);
    trigger(`items.${mainIndex}.parts.${rowIndex}.isLookUped`);

    setAvailabilityState({
      mainIndex: mainIndex,
      rowIndex: rowIndex,
      availability: part.availability
    });

    if (wipInfoId) {
      const result: StockPart[] = branches.map((branch) => {
        if (part?.availability?.length) {
          const matched = part?.availability?.find((x) => x.branch === branch.value);

          return {
            branch: branch.value,
            branchName: branch.label,
            branchCount: matched?.availableStock ?? 0,
            isNotFoundInCDK: matched?.isNotFoundInCDK ?? false
          };
        }

        return {
          branch: branch.value,
          branchName: branch.label,
          branchCount: 0,
          isNotFoundInCDK: false
        };
      });

      mutateBranch({
        id: wipInfoId,
        requestBody: result
      });
    }

    if (part.availability?.length) {
      const availability = initState?.branch
        ? part.availability.find((x) => x.branch == initState?.branch) ?? part.availability[0]
        : part.availability[0];

      if (isLoadedPartInfo.current) {
        setValue(`items.${mainIndex}.parts.${rowIndex}.price`, availability.listPrice?.grossValue ?? 0);
        trigger(`items.${mainIndex}.parts.${rowIndex}.price`);

        const quantity = getValues(`items.${mainIndex}.parts.${rowIndex}.quantity`) ?? 0;
        const discount = getValues(`items.${mainIndex}.parts.${rowIndex}.discount`) ?? 0;

        const total = availability.listPrice?.grossValue ?? 0 * quantity;
        const totalVAT = total - (discount / 100) * total;

        setValue(`items.${mainIndex}.parts.${rowIndex}.totalExclVat`, Number((totalVAT / 1.2).toFixed(2)));
        setValue(`items.${mainIndex}.parts.${rowIndex}.totalInclVat`, Number(totalVAT.toFixed(2)));
      }

      setValue(`items.${mainIndex}.parts.${rowIndex}.listPriceExclVAT`, availability.listPrice?.netValue ?? 0);
      setValue(`items.${mainIndex}.parts.${rowIndex}.listPriceInclVAT`, availability.listPrice?.grossValue ?? 0);

      if (!currentPart.surchargeExclVAT)
        setValue(`items.${mainIndex}.parts.${rowIndex}.surchargeExclVAT`, availability.surcharge?.netValue ?? 0);

      if (!currentPart.surchargeInclVAT)
        setValue(`items.${mainIndex}.parts.${rowIndex}.surchargeInclVAT`, availability.surcharge?.grossValue ?? 0);
    }
  };

  const openSelectPartsModelToggleState = useToggle<PartLookUpResult[]>(false);

  const handleSearch = async () => {
    if (!partNumber || !brandCode) {
      trigger(`items.${mainIndex}.parts.${rowIndex}.partNumber`);
      trigger(`items.${mainIndex}.parts.${rowIndex}.brandCode`);
    }

    await refetchPartRequestsPartsLookupQuery();
  };

  return (
    <>
      {openSelectPartsModelToggleState.opened && (
        <SelectPartModal
          state={openSelectPartsModelToggleState}
          onSelectPart={(part) => {
            applySelectedPart(part);
            openSelectPartsModelToggleState.close();
          }}
        />
      )}

      <div className="w-100">
        <div className="d-flex flex-nowrap gap-1 align-items-center w-100">
          <input
            tabIndex={1}
            autoFocus
            type="text"
            className={`form-control form-control-solid ${styles.partNumber} ${
              isDeleted || errors?.items?.[mainIndex]?.parts?.[rowIndex]?.partNumber ? 'border-danger' : ''
            }`}
            {...register(`items.${mainIndex}.parts.${rowIndex}.partNumber`)}
            disabled={!isEditMode || isDeleted || isLookUped}
            style={{ ...(isDeleted ? { backgroundColor: '#fff5f8' } : {}) }}
          />
          {!isDeleted && isEditMode && !isLookUped && (
            <button
              type="button"
              onClick={() => handleSearch()}
              className="btn btn-secondary btn-sm"
              disabled={isLoadingPartsLookup || !isEditMode || isLoadingSavePartLine || isLoadingBranch}
              style={{ width: '41px', height: '38px' }}
            >
              <InlineSvg path="/media/icons/custom/zoom_1.svg" className="svg-icon-2" />
            </button>
          )}
        </div>
        {errors?.items?.[mainIndex]?.parts?.[rowIndex]?.partNumber?.message && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">{errors.items[mainIndex]!.parts![rowIndex]!.partNumber?.message ?? ''}</div>
          </div>
        )}
      </div>
    </>
  );
};
